import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  COMPLETENESS_FLAG_LABELS,
  type Dashboard,
  PROFILE_CHECKLIST_LABELS,
  type ProfileChecklistItem,
  dashboardSchema,
  formatEmpCode,
} from '@thrivehrm/shared';
import { Check, Circle, CircleCheck, Inbox } from 'lucide-react';
import type * as React from 'react';
import { PersonAvatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { DateTile } from '@/components/ui/date-tile';
import { EmptyState } from '@/components/ui/empty-state';
import { MoneyValue, RevealToggle } from '@/components/ui/reveal-toggle';
import { StatTile } from '@/components/ui/stat-tile';
import { WorkItemList, WorkItemRow } from '@/components/ui/work-item-row';
import type { ProfileTab } from '@/features/employees/profile/tabs';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { useSalaryReveal } from '@/lib/reveal';
import { RowsSkeleton, StatTilesSkeleton, WidgetError } from './widget-states';

/**
 * Dashboard widgets (FR-DASH-1..3, ADR-044). All read the one `GET /dashboard` response (shared
 * query), which carries a section only when the user holds its permission; the module registry
 * shows each widget under the same permission.
 */
function useDashboard() {
  return useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: ({ signal }) => api.get<Dashboard>('/dashboard', { schema: dashboardSchema, signal }),
  });
}

/**
 * Renders `children` with the section, or the loading skeleton (`skeleton`, default rows), the
 * error Banner whose Retry refetches the shared query, or "Not available".
 */
function Section<K extends keyof Dashboard>({
  name,
  skeleton = <RowsSkeleton />,
  children,
}: {
  name: K;
  skeleton?: React.ReactNode;
  children: (section: NonNullable<Dashboard[K]>) => React.ReactNode;
}) {
  const { data, isPending, error, refetch, isRefetching } = useDashboard();
  if (isPending) return <>{skeleton}</>;
  if (error) return <WidgetError onRetry={() => void refetch()} retrying={isRefetching} />;
  const section = data[name];
  if (section === null) return <Muted>Not available.</Muted>;
  return <>{children(section)}</>;
}

const Muted = ({ children }: { children: React.ReactNode }) => (
  <p className="text-small text-muted-foreground">{children}</p>
);

/** Text link at the foot of a card ("Open my team"): 13 px, 600, primary. */
const linkClass = 'w-fit text-small font-semibold text-primary hover:underline';

const CHECKLIST_TAB: Readonly<Record<ProfileChecklistItem, ProfileTab>> = {
  DATE_OF_BIRTH: 'personal',
  GENDER: 'personal',
  BLOOD_GROUP: 'personal',
  PERSONAL_EMAIL: 'personal',
  NID_NUMBER: 'personal',
  NID_DOCUMENT: 'personal',
  PRESENT_ADDRESS: 'contact',
  PERMANENT_ADDRESS: 'contact',
  EMERGENCY_CONTACT: 'emergency',
  BANK_ACCOUNT: 'bank',
};

/** Progress ring (SVG, token colours); the percentage is also given as text. */
function Ring({ percent }: { percent: number }) {
  const r = 26;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 64 64" className="size-16 shrink-0" aria-hidden="true">
      <circle cx="32" cy="32" r={r} fill="none" strokeWidth="6" className="stroke-surface-muted" />
      <circle
        cx="32"
        cy="32"
        r={r}
        fill="none"
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - percent / 100)}
        transform="rotate(-90 32 32)"
        className="stroke-primary"
      />
    </svg>
  );
}

/** FR-DASH-1: profile completeness ring, "Complete your profile" checklist, own requests. */
export function MyProfileWidget() {
  return (
    <Section name="me">
      {(me) => {
        const todo = me.checklist.filter((c) => !c.done);
        return (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <Ring percent={me.percent} />
              <div>
                <p className="text-item text-foreground">Profile {me.percent}% complete</p>
                <p className="text-small text-muted-foreground">
                  {me.pendingRequests > 0
                    ? `${String(me.pendingRequests)} request${me.pendingRequests === 1 ? '' : 's'} waiting for review`
                    : 'No requests waiting'}
                </p>
              </div>
            </div>
            {todo.length > 0 ? (
              <ul
                aria-label="Complete your profile"
                className="grid gap-x-6 gap-y-2 sm:grid-cols-2"
              >
                {me.checklist.map(({ item, done }) => (
                  <li key={item} className="flex items-center gap-2 text-body">
                    {done ? (
                      <Check aria-hidden="true" className="size-4 shrink-0 text-success" />
                    ) : (
                      <Circle
                        aria-hidden="true"
                        className="size-4 shrink-0 text-subtle-foreground"
                      />
                    )}
                    {done ? (
                      <span className="text-muted-foreground">
                        {PROFILE_CHECKLIST_LABELS[item]}
                        <span className="sr-only"> (done)</span>
                      </span>
                    ) : (
                      <Link
                        to="/me"
                        search={{ tab: CHECKLIST_TAB[item] }}
                        className="font-semibold text-primary hover:underline"
                      >
                        Add {PROFILE_CHECKLIST_LABELS[item].toLowerCase()}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <Muted>Everything is filled in. Thank you!</Muted>
            )}
            <nav
              aria-label="Quick links"
              className="flex flex-wrap gap-x-5 gap-y-1 border-t pt-3 text-small font-semibold"
            >
              <Link to="/me" className="text-primary hover:underline">
                My profile
              </Link>
              <Link to="/me/salary" className="text-primary hover:underline">
                My salary
              </Link>
              <Link to="/me" search={{ tab: 'bank' }} className="text-primary hover:underline">
                Request a bank change
              </Link>
            </nav>
          </div>
        );
      }}
    </Section>
  );
}

/** FR-DASH-2: team size and direct reports. */
export function MyTeamWidget() {
  return (
    <Section name="team" skeleton={<StatTilesSkeleton count={2} />}>
      {(team) => (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-2">
            <StatTile label="In your team" value={team.size} />
            <StatTile label="Direct reports" value={team.directReports.length} />
          </div>
          <ul aria-label="Direct reports" className="flex flex-col gap-2.5">
            {team.directReports.slice(0, 6).map((m) => (
              <li key={m.id} className="flex items-center gap-3">
                <PersonAvatar name={m.fullName} />
                <span className="flex min-w-0 flex-1 flex-col">
                  <Link
                    to="/team/$employeeId"
                    params={{ employeeId: m.id }}
                    className="truncate text-body-strong text-primary hover:underline"
                  >
                    {m.fullName}
                  </Link>
                  {m.designation ? (
                    <span className="truncate text-caption text-muted-foreground">
                      {m.designation}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
          <Link to="/team" className={linkClass}>
            Open my team
          </Link>
        </div>
      )}
    </Section>
  );
}

/** FR-DASH-3: headcount by status and current staff by department (bar list). */
export function HeadcountWidget() {
  return (
    <Section name="headcount" skeleton={<StatTilesSkeleton count={4} />}>
      {(h) => {
        const max = Math.max(1, ...h.byDepartment.map((d) => d.count));
        return (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-2">
              <StatTile label="Active" value={h.active} />
              <StatTile label="Upcoming" value={h.upcoming} />
              <StatTile label="Notice period" value={h.noticePeriod} />
              <StatTile label="Exited" value={h.exited} />
            </div>
            <ul aria-label="Headcount by department" className="flex flex-col gap-2.5">
              {h.byDepartment.map((d) => (
                <li key={d.departmentId ?? 'none'} className="flex flex-col gap-1">
                  <div className="flex justify-between gap-2 text-small">
                    <span className="truncate text-foreground">{d.name ?? 'No department'}</span>
                    <span className="tabular font-semibold text-foreground">{d.count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-surface-muted" aria-hidden="true">
                    <div
                      className="h-2 rounded-full bg-primary"
                      style={{ width: `${String((d.count / max) * 100)}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
      }}
    </Section>
  );
}

function PeopleList({
  label,
  people,
  empty,
}: {
  label: string;
  people: NonNullable<Dashboard['movements']>['joiners'];
  empty: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-label text-foreground">
        {label} <span className="tabular text-muted-foreground">({people.length})</span>
      </h3>
      {people.length === 0 ? (
        <Muted>{empty}</Muted>
      ) : (
        <ul aria-label={label} className="flex flex-col gap-3">
          {people.map((p) => (
            <li key={p.id} className="flex items-center gap-3">
              <DateTile date={p.date} />
              <Link
                to="/employees/$employeeId"
                params={{ employeeId: p.id }}
                className="min-w-0 flex-1 truncate text-body-strong text-primary hover:underline"
              >
                {p.fullName}{' '}
                <span className="text-small font-normal text-muted-foreground">
                  {formatEmpCode(p.empCode)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** FR-DASH-3: joiners and leavers this month, each with a date tile. */
export function MovementsWidget() {
  return (
    <Section name="movements">
      {(m) => (
        <div className="grid gap-6 sm:grid-cols-2">
          <PeopleList label="Joining" people={m.joiners} empty="Nobody joins this month." />
          <PeopleList label="Leaving" people={m.leavers} empty="Nobody leaves this month." />
        </div>
      )}
    </Section>
  );
}

/**
 * FR-DASH-3: requests the user may decide, as a work item ("2 requests waiting for you" →
 * Review requests), or the "Nothing waiting on you" empty state.
 */
export function PendingApprovalsWidget() {
  return (
    <Section name="approvals">
      {(a) =>
        a.pending === 0 ? (
          <EmptyState
            icon={CircleCheck}
            title="Nothing waiting on you"
            hint="New requests show up here as soon as they are assigned to you."
            className="py-8"
            action={
              <Link to="/approvals" className={linkClass}>
                Open approvals
              </Link>
            }
          />
        ) : (
          <WorkItemList aria-label="Waiting on you">
            <WorkItemRow
              icon={Inbox}
              tone="warning"
              meta="Approvals"
              title={`${String(a.pending)} request${a.pending === 1 ? '' : 's'} waiting for you`}
              detail="Open each request to approve or reject it."
              actions={
                <Button asChild size="sm">
                  <Link to="/approvals">Review requests</Link>
                </Button>
              }
            />
          </WorkItemList>
        )
      }
    </Section>
  );
}

/** FR-DASH-3: people (not exited) missing data, per flag the user may see (ADR-037). */
export function DataGapsWidget() {
  return (
    <Section name="dataGaps">
      {(gaps) =>
        gaps.length === 0 ? (
          <Muted>Nothing to show.</Muted>
        ) : (
          <ul aria-label="Missing data" className="flex flex-col divide-y">
            {gaps.map((g) => (
              <li key={g.flag} className="flex justify-between gap-2 py-2 text-body">
                <span className="text-foreground">{COMPLETENESS_FLAG_LABELS[g.flag]}</span>
                <span
                  className={
                    g.count > 0
                      ? 'tabular font-semibold text-warning'
                      : 'tabular text-muted-foreground'
                  }
                >
                  {g.count}
                </span>
              </li>
            ))}
          </ul>
        )
      }
    </Section>
  );
}

/** FR-DASH-3, AC-3: today's payroll totals (salary.read.all), masked until revealed (FR-SAL-8). */
export function PayrollWidget() {
  const [revealed, setRevealed] = useSalaryReveal();
  return (
    <Section name="payroll" skeleton={<StatTilesSkeleton count={3} />}>
      {(p) => (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-small text-muted-foreground">
              {p.employees} people on today’s salaries
            </p>
            <RevealToggle revealed={revealed} onRevealedChange={setRevealed} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <StatTile
              label="Gross"
              value={<MoneyValue amount={p.gross} revealed={revealed} />}
              className="col-span-2"
            />
            <StatTile
              label="Through bank"
              value={<MoneyValue amount={p.acAmount} revealed={revealed} />}
            />
            <StatTile
              label="In cash"
              value={<MoneyValue amount={p.cashAmount} revealed={revealed} />}
            />
          </div>
        </div>
      )}
    </Section>
  );
}
